/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { DatePickerInput } from '@components/v2/ui/DatePickerInput/DatePickerInput';
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import dayjs from 'dayjs';

// Apis
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { useBrands } from 'shared/api/get-brands';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	entity?: string[];
	[key: string]: unknown;
	createdAt?: {
		$gte: string | Date;
		$lt: string | Date;
	};
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */

	const { t } = useTranslation();

	/* ******************************** APIs ******************************* */
	const { data: brands } = useBrands();

	const { checkRole, checkAcls, role } =
		useAuthorization();

	const { setOffsetsQueryParams, setOffsetsPage } =
		useStore();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setOffsetsQueryParams({});
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const handleApplyFilters = () => {
		setOffsetsQueryParams(queryParams);
		setOffsetsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					{checkAclFront(
						ACLS.FRONT.OFFSETS.FILTER_DATE,
						role as ROLES
					) && (
						<DatePickerInput
							value={[
								queryParams.createdAt?.$gte ?? '',
								queryParams.createdAt?.$lt ?? '',
							]}
							onChange={(value) => {
								if (
									value.length === 2 &&
									value[0] === null &&
									value[1] === null
								) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['createdAt'];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									createdAt: {
										$gte: value[0] ?? '',
										$lt: value[1] ?? '',
									},
								});
							}}
							size="md"
							placeholder={t('filters.titles.perDate')}
							clearable
							type="range"
							presets={[
								{
									value: [
										dayjs()
											.startOf('week')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('week')
											.format('YYYY-MM-DD'),
									],
									label: 'This week',
								},
								{
									value: [
										dayjs()
											.startOf('month')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('month')
											.format('YYYY-MM-DD'),
									],
									label: 'This month',
								},
								{
									value: [
										dayjs()
											.startOf('year')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('year')
											.format('YYYY-MM-DD'),
									],
									label: 'This year',
								},
							]}
						/>
					)}

					{checkAclFront(
						ACLS.FRONT.OFFSETS.FILTER_BRANDS,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t('global.brands')}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={convertArrayToLabelValue(brands)}
							clearable
							size="lg"
							value={queryParams.entity ?? []}
							searchable
							onChange={(value) => {
								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['entity'];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									entity: value,
								});
							}}
						/>
					)}
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
